import React, { useEffect, useRef, useState } from 'react';
import { ValueState } from '@ui5/webcomponents-react';
import {
  TranslationType,
  useGetProjectDetailsQuery,
  useGetProjectsByTranslationTypeQuery
} from 'generated/graphql';
import { useHistory } from 'react-router';
import { CreateQueryView } from './CreateQueryView';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { validateString } from 'utils/validator';
import { SpecificQueryTypeForCreate } from 'components/shared/interfaces/SpecificQueryTypes';
import { NonAbapQuery } from './NonAbapQuery';
import { DitaQuery } from './DitaQuery';
import { TrainingQuery } from './TrainingQuery';
import { CorporateContentQuery } from './CorporateContentQuery';
import { CreateQueryProvider } from './CreateQueryContext/CreateQueryContext';
import { CombinedError } from '@urql/core';
import { AttachmentErrorCode } from 'enum/AttachmentErrorCode';
import { Prompt } from 'react-router-dom';
import { isMatch } from 'lodash';
import { commonBaseQueryInitialStateFields } from 'stores/slices/BaseQuerySlices/CommonBaseQuerySlice';
import { createBaseQueryInitialStateFields } from 'stores/slices/BaseQuerySlices/CreateBaseQuerySlice';
import { useBeforeWindowUnload } from 'hooks/useBeforeWindowUnload';
import { useGetDate } from 'hooks/useGetDate';
import dayjs from 'dayjs';
import {
  IssueTypeOptions,
  NotSelectedTranslationType,
  TranslationTypeOptions
} from '../../stores/interfaces/CommonQueryState';
import { ABAPQuery } from './ABAP/ABAPQuery';
import { useUserViewStore } from 'stores/UserViewStore';
import { useParametrizeQueryCreation } from 'hooks/useParametrizeQueryCreation';
import { useDitaQueryStore } from 'stores/CreateDitaQueryStore';
import { useABAPQueryStore } from 'stores/CreateABAPQueryStore';
import { useNonAbapQueryStore } from 'stores/CreateNonAbapQueryStore';
import { useTrainingQueryStore } from 'stores/CreateTrainingQueryStore';
import { NotesQuery } from './NotesQuery';
import { useNotesQueryStore } from 'stores/CreateNotesQueryStore';
import { useMarketingQueryStore } from 'stores/CreateMarketingQueryStore';
import { MarketingQuery } from './MarketingQuery';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

export const CreateQuery: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const toastRef = useRef<any>();
  const specificRef = useRef<SpecificQueryTypeForCreate>(null);

  const [toastMessage, setToastMessage] = useState('');
  const TOAST_DURATION = 3000;
  const FILES_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.zip', '.pdf'];

  /** States */
  const store = useBaseQueryStore();
  const ditaStore = useDitaQueryStore();
  const abapStore = useABAPQueryStore();
  const nonAbapStore = useNonAbapQueryStore();
  const corporateContentStore = useNonAbapQueryStore();
  const trainingStore = useTrainingQueryStore();
  const notesStore = useNotesQueryStore();
  const marketingStore = useMarketingQueryStore();
  const { fillCreateQueryFormWithParams } = useParametrizeQueryCreation();
  const freezeStoreEditParam = useCreateQueryFreezeStore((store) => store.editParam);
  const unfreezeInputs = useCreateQueryFreezeStore((store) => store.unfreeze);

  const [currentTranslationType, setCurrentTranslationType] = useState<
    keyof TranslationTypeOptions
  >(createBaseQueryInitialStateFields.selectedTranslationType);

  const [projectNames] = useGetProjectsByTranslationTypeQuery({
    variables: {
      translationType: currentTranslationType as TranslationType
    },
    pause: currentTranslationType === TranslationTypeOptions.NOT_SELECTED_TRANSLATION_TYPE
  });

  useEffect(() => {
    fillCreateQueryFormWithParams();
  }, [freezeStoreEditParam]);

  useEffect(() => {
    setCurrentTranslationType(store.selectedTranslationType);
  }, [store.selectedTranslationType]);

  const [projectDetails] = useGetProjectDetailsQuery({
    pause: store.selectedProject === -1,
    variables: {
      id: store.selectedProject
    }
  });

  const { getDefaultDeadline, getFormattedDateString, getDatePlusDays } = useGetDate();

  const leavingPageAlert = (e: BeforeUnloadEvent) => {
    if (containsRelevantChanges()) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  const containsRelevantChangesCommonFields = () => {
    const baseStore: any = {
      ...commonBaseQueryInitialStateFields,
      ...createBaseQueryInitialStateFields
    };
    delete baseStore.selectedTranslationType;
    delete baseStore.deadline;
    return !isMatch(store, baseStore);
  };

  const containsRelevantChanges = () => {
    return containsRelevantChangesCommonFields() || specificRef.current?.containsRelevantChanges();
  };

  const shouldResetProject = () => {
    return TranslationType.TRAINING !== currentTranslationType;
  };

  const setShouldUseSessionVariant = useUserViewStore((state) => state.setShouldUseSessionVariant);

  useBeforeWindowUnload(leavingPageAlert);

  useEffect(() => {
    // use the session's selected view when going back to manage queries page
    setShouldUseSessionVariant(true);

    return () => {
      unfreezeInputs();
      store.resetAllData();
    };
  }, []);

  useEffect(() => {
    setDefaultDeadline();
  }, [store.selectedTranslationType]);

  useEffect(() => {
    store.setProjectDetails(projectDetails);
    setProjectDetails();
  }, [projectDetails]);

  useEffect(() => {
    store.setProjectNames(projectNames);
  }, [projectNames.data?.projects]);

  /** Change functions */
  const setProjectDetails = () => {
    const projectManagerName = projectDetails.data?.project?.projectManager || '';

    store.setProjectManager(projectManagerName);
  };

  const setDefaultDeadline = () => {
    const defaultDeadline = getDefaultDeadline();
    const marketingQueryDeadline = getDatePlusDays(1).toISOString();
    const isMarketingQuery = store.selectedTranslationType === TranslationType.MARKETING;

    const deadline = isMarketingQuery
      ? getFormattedDateString(marketingQueryDeadline)
      : getFormattedDateString(defaultDeadline);

    store.setDeadline(deadline);
    store.setAnswerDeadline(deadline);
  };

  /** Validation functions */
  const validateProject = () => {
    if (store.selectedProject > 0) {
      store.setProjectValueState(ValueState.Success);
      return true;
    } else {
      store.setProjectValueState(ValueState.Error);
      return false;
    }
  };

  const validateTargetLanguage = () => {
    return validateString(store.targetLanguage, store.setTargetLanguageValueState);
  };

  const validateDeadline = () => {
    if (
      store.deadline &&
      dayjs(store.deadline).isValid() &&
      store.deadlineValueState !== ValueState.Error
    ) {
      store.setDeadlineValueState(ValueState.Success);
      return true;
    } else {
      store.setDeadlineValueState(ValueState.Error);
      return false;
    }
  };

  const validateSourceText = () => {
    return validateString(store.sourceText, store.setSourceTextValueState);
  };

  const validateIssueType = () => {
    if (store.selectedIssueType === IssueTypeOptions.NOT_SELECTED_ISSUE_TYPE) {
      store.setIssueTypeValueState(ValueState.Error);
      return false;
    } else {
      return validateString(store.selectedIssueType, store.setIssueTypeValueState);
    }
  };

  const validateTranslationType = () => {
    if (
      store.selectedTranslationType === NotSelectedTranslationType.NOT_SELECTED_TRANSLATION_TYPE
    ) {
      store.setTranslationTypeValueState(ValueState.Error);
      return false;
    }

    store.setTranslationTypeValueState(ValueState.Success);
    return true;
  };

  const validateBaseQuery = (): boolean => {
    const isProjectValid = validateProject();
    const isTargetLanguageValid = validateTargetLanguage();
    const isDeadlineValid = validateDeadline();
    const isSourceTextValid = validateSourceText();
    const isIssueTypeValid = validateIssueType();
    const isTranslationTypeValid = validateTranslationType();
    return (
      isProjectValid &&
      isTargetLanguageValid &&
      isDeadlineValid &&
      isSourceTextValid &&
      isIssueTypeValid &&
      isTranslationTypeValid
    );
  };

  /** Save functions */
  const onClickCreate = async () => {
    if (isQueryValid()) {
      const saveResult = await specificRef.current?.save();
      showSaveResultMessage(saveResult?.error);
      if (!saveResult?.error) {
        store.resetAllData();
        navigateToQueries(saveResult?.id);
      }
    } else {
      showFieldValidationMessage();
    }
  };

  const navigateToQueries = (queryId?: string) => {
    setTimeout(() => {
      queryId ? history.push(`/queries/${queryId}`) : history.push('/queries');
    }, 0);
  };

  const showFieldValidationMessage = () => {
    setToastMessage(t('create-query-check-input-toast-message'));
    if (toastRef.current) {
      toastRef.current.show();
    }
  };

  const onClickCreateAndNew = async () => {
    if (isQueryValid()) {
      const saveResult = await specificRef.current?.save();
      showSaveResultMessage(saveResult?.error);
      if (!saveResult?.error) {
        store.resetDataForNewQuery(shouldResetProject());
      }
    } else {
      showFieldValidationMessage();
    }
  };

  const isQueryValid = () => {
    const isBaseQueryValid = validateBaseQuery();
    return specificRef.current && specificRef.current.isValid() && isBaseQueryValid;
  };

  const onClickCancel = () => {
    specificRef.current?.cancel();
    navigateToQueries();
  };

  const showSaveResultMessage = (combinedError?: CombinedError) => {
    if (!combinedError) {
      setToastMessage(t('create-query-create-successfully-toast-message'));
    } else {
      setErrorMessage(combinedError);
    }
    if (toastRef.current) {
      toastRef.current.show();
    }
  };

  const setErrorMessage = (combinedError?: CombinedError) => {
    const errorMessage = combinedError?.message.replace('[GraphQL] ', '') ?? '';
    const [errorCode] = errorMessage.split(' ').map((str) => str.trim());
    let message = '';
    switch (errorCode) {
      case AttachmentErrorCode.FILE_SIZE_EXCEEDED:
        message = t('attachment-file-size-exceed');
        setAttachmentErrorMessage(message);
        break;
      case AttachmentErrorCode.QUERY_NOT_FOUND:
        message = t('attachment-query-not-found');
        setAttachmentErrorMessage(message);
        break;
      case AttachmentErrorCode.UPLOAD_ERROR:
        message = t('attachment-file-upload-error');
        setAttachmentErrorMessage(message);
        break;
      case AttachmentErrorCode.SAVE_ATTACHMENT_ERROR:
        message = t('attachment-save-error');
        setAttachmentErrorMessage(message);
        break;
      case AttachmentErrorCode.INVALID_MIME_TYPE:
        message = t('attachment-invalid-mime-type', {
          'accepted-mimetypes': FILES_EXTENSIONS.join(', ')
        });
        setAttachmentErrorMessage(message);
        break;
      case AttachmentErrorCode.MALWARE_DETECTED:
        message = t('attachment-malware-detected');
        setAttachmentErrorMessage(message);
        break;
      default:
        message = t('create-query-error-creating-toast-message');
        break;
    }
    setToastMessage(message);
  };

  const setAttachmentErrorMessage = (message: string) => {
    setToastMessage(message);
    store.setAttachmentValueState(ValueState.Warning);
    store.setAttachmentValueStateMessage(message);
  };

  const getSpecificQuery = (): React.ReactNode => {
    switch (store.selectedTranslationType) {
      case TranslationType.NON_ABAP: {
        return <NonAbapQuery ref={specificRef} />;
      }
      case TranslationType.DITA: {
        return <DitaQuery ref={specificRef} />;
      }
      case TranslationType.TRAINING: {
        return <TrainingQuery ref={specificRef} />;
      }
      case TranslationType.CORPORATE_CONTENT: {
        return <CorporateContentQuery ref={specificRef} />;
      }
      case TranslationType.ABAP: {
        return <ABAPQuery ref={specificRef} />;
      }
      case TranslationType.NOTES: {
        return <NotesQuery ref={specificRef} />;
      }
      case TranslationType.MARKETING: {
        return <MarketingQuery ref={specificRef} />;
      }
      case NotSelectedTranslationType.NOT_SELECTED_TRANSLATION_TYPE:
        return null;
    }
  };

  const resetFields = () => {
    store.resetDataForNewQuery(shouldResetProject());
    trainingStore.resetData();
    ditaStore.resetData();
    abapStore.resetData();
    nonAbapStore.resetData();
    corporateContentStore.resetData();
    notesStore.resetData();
    marketingStore.resetData();
  };

  return (
    <CreateQueryProvider type={store.selectedTranslationType}>
      <CreateQueryView
        onClickCancel={onClickCancel}
        onClickCreate={onClickCreate}
        onClickCreateAndNew={onClickCreateAndNew}
        resetFields={resetFields}
        toastRef={toastRef}
        toastMessage={toastMessage}
        toastDuration={TOAST_DURATION}
        specificQuery={getSpecificQuery()}
      />
      <Prompt
        message={() => {
          if (containsRelevantChanges()) {
            return t('create-query-before-leave-prompt-message') as unknown as string;
          }
          return true;
        }}
      />
    </CreateQueryProvider>
  );
};
